import "./GraphUnit.scss";
// REACT; ROUTER
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

// COMPONENTS & FUNCTIONS
import {
  createEmptyGraphLegend,
  GraphSource,
  resolveGraphConfiguration,
} from "../../functions/graphHandler";
import SortableTableTool, {
  SortableTableColumn,
} from "../tools/SortableTableTool";

// SVG & IMAGES

// INTERFACES
import {
  GraphConfiguration,
  GraphControls,
  GraphLegend,
  GraphSavedPoint,
} from "../../interface/functionalityInterface";

// NPM INSTALLS
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import * as FaIcons from "react-icons/fa";
import { v4 as UUID } from "uuid";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

interface GraphUnitProps {
  source: GraphSource;
  savedConfiguration?: GraphConfiguration;
  saveConfiguration: (
    configuration: GraphConfiguration
  ) => void;
  closeGraph: () => void;
}

/*
GRAPHUNIT COMPONENT INFORMATION
  GraphUnit is the shared graph interface for standard and advanced calculators.
  - The calculator supplies variables, calculated data and constants through a
    GraphSource. Constants remain owned and edited by the calculator.
  - GraphConfiguration is serializable and saved as part of the calculator.
  - Every legend selects its own X and Y variables, allowing one or many lines.
  - X delta controls axis ticks. Cursor tracking increment controls the number
    of calculated hover points and is limited to a maximum of 1000 points.
*/

const labelFor = (name: string) => name.replaceAll("_", " ");
const valuesMatch = (
  firstValue: number,
  secondValue: number,
  increment: number
) =>
  Math.abs(firstValue - secondValue) <=
  Math.max(Math.abs(increment) / 1000, 0.000000001);

const formatSavedValue = (
  value: number,
  unit: string,
  decimals: number
) => `${value.toFixed(decimals)}${unit ? ` ${unit}` : ""}`;

export default function GraphUnit({
  source,
  savedConfiguration,
  saveConfiguration,
  closeGraph,
}: GraphUnitProps) {
  // ::::: STATES :::::
  const [configuration, setConfiguration] =
    useState<GraphConfiguration>(() =>
      resolveGraphConfiguration(source, savedConfiguration)
    );
  const configurationRef = useRef(configuration);
  const saveConfigurationRef = useRef(saveConfiguration);
  const closeGraphRef = useRef(closeGraph);
  const previousOverflowRef = useRef("");
  configurationRef.current = configuration;
  saveConfigurationRef.current = saveConfiguration;
  closeGraphRef.current = closeGraph;

  // ::::: CONSTANTS :::::
  const results = useMemo(
    () =>
      configuration.legends.map((legend) =>
        source.calculateLegend(legend, configuration)
      ),
    [configuration, source]
  );
  const validResults = results.filter((result) => !result.error);
  const graphErrors = results
    .filter((result) => result.error)
    .map((result) => result.error!);
  const xVariables = configuration.legends
    .map((legend) =>
      source.variables.find(
        (variable) => variable.key === legend.xKey
      )
    )
    .flatMap((variable) => (variable ? [variable] : []));
  const xUnits = Array.from(
    new Set(xVariables.map((variable) => variable.selectedUnit))
  );
  const hasMixedXUnits = xUnits.length > 1;
  const xAxisName =
    xVariables.length === 0
      ? "X"
      : new Set(xVariables.map((variable) => variable.label)).size === 1
      ? xVariables[0].label
      : "Multiple X variables";
  const xAxisLabel =
    xVariables.length === 0
      ? "X"
      : `${xAxisName} (${xUnits[0] || "–"})`;
  const legendLabel = (legend: GraphLegend) => {
    const yVariable = source.variables.find(
      (variable) => variable.key === legend.yKey
    );
    const xVariable = source.variables.find(
      (variable) => variable.key === legend.xKey
    );

    return `${yVariable?.label ?? labelFor(legend.yKey)} vs ${
      xVariable?.label ?? labelFor(legend.xKey)
    } (${yVariable?.selectedUnit || "–"})`;
  };

  const chartData: ChartData<
    "line",
    { x: number; y: number | null }[]
  > = {
    datasets: configuration.legends.flatMap((legend) => {
      const result = validResults.find(
        (candidate) => candidate.legendId === legend.id
      );
      if (!result) return [];

      return [
        {
          label: legendLabel(legend),
          data: result.points,
          borderColor: legend.color,
          backgroundColor: "transparent",
          borderDash: legend.borderDash,
          borderWidth: 2,
          pointRadius: (context) => {
            const point = context.raw as {
              x: number;
              y: number | null;
            };
            return configuration.savedPoints.some((savedPoint) =>
              valuesMatch(
                savedPoint.xValue,
                point.x,
                configuration.controls.trackingIncrement
              )
            )
              ? 4
              : 0;
          },
          pointHoverRadius: 4,
          spanGaps: false,
          yAxisID: `y-${legend.id}`,
        },
      ];
    }),
  };
  const savedSeries = Array.from(
    new Map(
      configuration.savedPoints
        .flatMap((point) => point.values)
        .map((value) => [
          value.key,
          {
            key: value.key,
            label: value.label,
            unit: value.unit,
          },
        ])
    ).values()
  );
  const savedPointColumns: SortableTableColumn<GraphSavedPoint>[] = [
    {
      key: "x",
      heading: configuration.savedPoints[0]
        ? `${configuration.savedPoints[0].xLabel} (${
            configuration.savedPoints[0].xUnit || "–"
          })`
        : xAxisLabel,
      render: (point) =>
        formatSavedValue(
          point.xValue,
          point.xUnit,
          configuration.controls.xDecimals
        ),
      sortValue: (point) => point.xValue,
    },
    ...savedSeries.map((series) => ({
      key: series.key,
      heading: series.label,
      render: (point: GraphSavedPoint) => {
        const savedValue = point.values.find(
          (value) => value.key === series.key
        );
        return savedValue
          ? formatSavedValue(
              savedValue.value,
              savedValue.unit,
              source.noDecimals
            )
          : "–";
      },
      sortValue: (point: GraphSavedPoint) =>
        point.values.find((value) => value.key === series.key)?.value,
    })),
  ];

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    normalized: true,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    onClick: (_event, elements) => {
      const selectedPoint = elements[0];
      if (!selectedPoint) return;
      toggleSavedPoint(
        selectedPoint.datasetIndex,
        selectedPoint.index
      );
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 18 },
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const xValue = items[0]?.parsed.x;
            return Number.isFinite(xValue)
              ? `X: ${xValue.toFixed(
                  configuration.controls.xDecimals
                )}${xUnits[0] ? ` ${xUnits[0]}` : ""}`
              : "";
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: configuration.controls.xMin,
        max: configuration.controls.xMax,
        title: {
          display: true,
          text: xAxisLabel,
        },
        ticks: {
          stepSize: configuration.controls.xDelta,
          callback: (value) =>
            Number(value).toFixed(
              configuration.controls.xDecimals
            ),
        },
      },
      ...Object.fromEntries(
        configuration.legends.map((legend, index) => {
          const yVariable = source.variables.find(
            (variable) => variable.key === legend.yKey
          );
          return [
            `y-${legend.id}`,
            {
              type: "linear",
              display: index < 2,
              position: index % 2 === 0 ? "left" : "right",
              min: configuration.controls.yMin ?? undefined,
              max: configuration.controls.yMax ?? undefined,
              title: {
                display: index < 2,
                text: `${yVariable?.label ?? labelFor(legend.yKey)} (${
                  yVariable?.selectedUnit || "–"
                })`,
              },
              grid:
                index === 0
                  ? undefined
                  : { drawOnChartArea: false },
            },
          ];
        })
      ),
    },
  };

  // ::::: HANDLER FUNCTIONS :::::
  const updateLegend = (
    legendId: string,
    key: "xKey" | "yKey",
    value: string
  ) => {
    setConfiguration((current) => ({
      ...current,
      legends: current.legends.map((legend) =>
        legend.id === legendId
          ? { ...legend, [key]: value }
          : legend
      ),
    }));
  };

  const addLegend = () => {
    setConfiguration((current) => ({
      ...current,
      legends: [
        ...current.legends,
        createEmptyGraphLegend(source, current.legends.length),
      ],
    }));
  };

  const removeLegend = (legendId: string) => {
    setConfiguration((current) => ({
      ...current,
      legends: current.legends.filter(
        (legend) => legend.id !== legendId
      ),
    }));
  };

  const updateControl = (
    key: keyof GraphControls,
    value: string
  ) => {
    const nullableControl = key === "yMin" || key === "yMax";
    const numericValue =
      nullableControl && value === ""
        ? null
        : key === "xDecimals"
        ? Math.max(0, Math.min(9, Number(value)))
        : Number(value);

    setConfiguration((current) => ({
      ...current,
      controls: {
        ...current.controls,
        [key]: numericValue,
      },
    }));
  };

  const toggleSavedPoint = (
    datasetIndex: number,
    pointIndex: number
  ) => {
    const selectedDataset = chartData.datasets[datasetIndex];
    const selectedPoint = selectedDataset?.data[pointIndex];
    if (
      !selectedPoint ||
      !Number.isFinite(selectedPoint.x) ||
      selectedPoint.y == null
    ) {
      return;
    }

    setConfiguration((current) => {
      const existingPoint = current.savedPoints.find((point) =>
        valuesMatch(
          point.xValue,
          selectedPoint.x,
          current.controls.trackingIncrement
        )
      );
      if (existingPoint) {
        return {
          ...current,
          savedPoints: current.savedPoints.filter(
            (point) => point.id !== existingPoint.id
          ),
        };
      }

      const values = chartData.datasets.flatMap((dataset) => {
        const nearestPoint = dataset.data.reduce<
          { x: number; y: number | null } | undefined
        >((nearest, point) => {
          if (point.y == null || !Number.isFinite(point.y)) {
            return nearest;
          }
          if (!nearest) return point;
          return Math.abs(point.x - selectedPoint.x) <
            Math.abs(nearest.x - selectedPoint.x)
            ? point
            : nearest;
        }, undefined);
        if (!nearestPoint || nearestPoint.y == null) return [];

        const legendId = String(dataset.yAxisID ?? "").replace(
          /^y-/,
          ""
        );
        const legend = current.legends.find(
          (candidate) => candidate.id === legendId
        );
        const yVariable = source.variables.find(
          (variable) => variable.key === legend?.yKey
        );
        const label = String(dataset.label ?? "Y");

        return [
          {
            key: label,
            label,
            unit: yVariable?.selectedUnit ?? "",
            value: nearestPoint.y,
          },
        ];
      });

      return {
        ...current,
        savedPoints: [
          ...current.savedPoints,
          {
            id: UUID(),
            xValue: selectedPoint.x,
            xLabel: xAxisName,
            xUnit: xUnits[0] ?? "",
            values,
          },
        ],
      };
    });
  };

  const togglePointsPanel = () => {
    setConfiguration((current) => ({
      ...current,
      isPointsPanelOpen: !current.isPointsPanelOpen,
    }));
  };

  const removeSavedPoint = (point: GraphSavedPoint) => {
    setConfiguration((current) => ({
      ...current,
      savedPoints: current.savedPoints.filter(
        (savedPoint) => savedPoint.id !== point.id
      ),
    }));
  };

  const selectTab = (
    activeTab: GraphConfiguration["activeTab"]
  ) => {
    setConfiguration((current) => ({
      ...current,
      activeTab,
    }));
  };

  const handleClose = useCallback(() => {
    document.documentElement.style.overflow =
      previousOverflowRef.current;
    saveConfigurationRef.current(configurationRef.current);
    closeGraphRef.current();
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    previousOverflowRef.current =
      document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      document.documentElement.style.overflow =
        previousOverflowRef.current;
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleClose]);

  // ::::: JSX :::::
  const content = (
    <div className="graph-unit">
      {/* ---------------------- GRAPH ----------------------*/}
      <div className="graph-unit__visual-shell">
        <div className="graph-unit__visual">
          <h3>{source.title}</h3>
          <div className="graph-unit__canvas">
            {configuration.legends.length === 0 ? (
              <p className="graph-unit__message">
                Add a legend to show graph data.
              </p>
            ) : hasMixedXUnits ? (
              <p className="graph-unit__message">
                <span
                  className="graph-unit__message-explanation"
                  data-tooltip="All legends share one X-axis. X variables such as diameter (mm) and velocity (m/s) represent different physical dimensions and cannot use the same numerical axis. The selected Y units do not affect this validation."
                >
                  All legend X variables must use compatible display
                  units.
                </span>
              </p>
            ) : validResults.length === 0 ? (
              <p className="graph-unit__message">
                {graphErrors[0] ?? "No graph data is available."}
              </p>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>
      </div>

      <span className="graph-unit__separator" aria-hidden="true"></span>

      {/* ---------------------- CONFIGURATION TABS ----------------------*/}
      <div
        className={`graph-unit__tabs graph-unit__tabs--${configuration.activeTab}`}
        role="tablist"
        aria-label="Graph configuration"
      >
        <span aria-hidden="true"></span>
        <button
          type="button"
          role="tab"
          aria-selected={configuration.activeTab === "legends"}
          className={
            configuration.activeTab === "legends" ? "active" : ""
          }
          onClick={() => selectTab("legends")}
        >
          legends
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={configuration.activeTab === "controls"}
          className={
            configuration.activeTab === "controls" ? "active" : ""
          }
          onClick={() => selectTab("controls")}
        >
          graph controls
        </button>
      </div>

      <div className="graph-unit__configuration">
        {/* ---------------------- LEGENDS ----------------------*/}
        <div
          className={`graph-unit__panel ${
            configuration.activeTab === "legends" ? "active" : ""
          }`}
          role="tabpanel"
        >
          <div className="graph-unit__panel-heading">
            <div>
              <h3>legends</h3>
              <p>
                Select X and Y. Remaining equation values are read from
                the calculator.
              </p>
            </div>
            <button
              type="button"
              className="rectangular-button small inverse-color"
              onClick={addLegend}
            >
              <FaIcons.FaPlus />
              <span>add legend</span>
            </button>
          </div>

          <div className="graph-unit__legend-list">
            {configuration.legends.map((legend, index) => {
              const result = results.find(
                (candidate) => candidate.legendId === legend.id
              );
              return (
                <div className="graph-unit__legend" key={legend.id}>
                  <h4 className="graph-unit__legend-heading">
                    {legendLabel(legend)}
                  </h4>
                  <fieldset>
                    <label htmlFor={`${legend.id}-x`}>
                      x<sub>{index + 1}</sub>
                    </label>
                    <select
                      id={`${legend.id}-x`}
                      aria-label={`x${index + 1}`}
                      className="calculator__calcmode"
                      value={legend.xKey}
                      onChange={(event) =>
                        updateLegend(
                          legend.id,
                          "xKey",
                          event.target.value
                        )
                      }
                    >
                      {source.variables
                        .filter((variable) => variable.canUseAsX)
                        .map((variable) => (
                          <option value={variable.key} key={variable.key}>
                            {variable.label} ({variable.selectedUnit || "–"})
                          </option>
                        ))}
                    </select>
                  </fieldset>
                  <fieldset>
                    <label htmlFor={`${legend.id}-y`}>
                      y<sub>{index + 1}</sub>
                    </label>
                    <select
                      id={`${legend.id}-y`}
                      aria-label={`y${index + 1}`}
                      className="calculator__calcmode"
                      value={legend.yKey}
                      onChange={(event) =>
                        updateLegend(
                          legend.id,
                          "yKey",
                          event.target.value
                        )
                      }
                    >
                      {source.variables
                        .filter((variable) => variable.canUseAsY)
                        .map((variable) => (
                          <option value={variable.key} key={variable.key}>
                            {variable.label} ({variable.selectedUnit || "–"})
                          </option>
                        ))}
                    </select>
                  </fieldset>
                  <button
                    type="button"
                    className="graph-unit__delete-legend svg-button"
                    onClick={() => removeLegend(legend.id)}
                    data-tooltip={`remove legend ${index + 1}`}
                    aria-label={`Remove legend ${index + 1}`}
                  >
                    <FaIcons.FaTrash />
                  </button>
                  {result?.error && (
                    <p className="graph-unit__legend-error">
                      {result.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------------- GRAPH CONTROLS ----------------------*/}
        <div
          className={`graph-unit__panel ${
            configuration.activeTab === "controls" ? "active" : ""
          }`}
          role="tabpanel"
        >
          <div className="graph-unit__panel-heading">
            <div>
              <h3>graph controls</h3>
              <p>
                Set the visible range, axis spacing and cursor
                resolution.
              </p>
            </div>
          </div>
          <div className="graph-unit__control-grid">
            {(
              [
                ["xMin", "x min"],
                ["xMax", "x max"],
                ["yMin", "y min"],
                ["yMax", "y max"],
                ["xDelta", "x delta"],
                ["trackingIncrement", "cursor increment"],
                ["xDecimals", "x decimals"],
              ] as [keyof GraphControls, string][]
            ).map(([key, label]) => (
              <fieldset key={key}>
                <label htmlFor={`${source.id}-graph-${key}`}>
                  {label}
                </label>
                <input
                  id={`${source.id}-graph-${key}`}
                  type="number"
                  step={key === "xDecimals" ? "1" : "any"}
                  min={
                    key === "xDecimals"
                      ? "0"
                      : key === "xDelta" ||
                        key === "trackingIncrement"
                      ? "0"
                      : undefined
                  }
                  max={key === "xDecimals" ? "9" : undefined}
                  value={configuration.controls[key] ?? ""}
                  onChange={(event) =>
                    updateControl(key, event.target.value)
                  }
                />
              </fieldset>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div className="graph-unit-popup">
      <aside
        id={`${source.id}-graph`}
        className="popup visible center"
      >
        <div className="graph-unit-popup__toolbar">
          <button
            className="close-button"
            type="button"
            onClick={handleClose}
            aria-label="Close graph"
          ></button>
          <button
            className={`graph-unit-popup__points-toggle round-button smaller ${
              configuration.isPointsPanelOpen ? "active" : ""
            }`}
            type="button"
            onClick={togglePointsPanel}
            aria-label={
              configuration.isPointsPanelOpen
                ? "Hide saved points"
                : "Show saved points"
            }
            aria-expanded={configuration.isPointsPanelOpen}
            aria-controls={`${source.id}-saved-points`}
            data-tooltip={
              configuration.isPointsPanelOpen
                ? "hide saved points"
                : "show saved points"
            }
          >
            {configuration.isPointsPanelOpen ? (
              <FaIcons.FaBookmark />
            ) : (
              <FaIcons.FaRegBookmark />
            )}
          </button>
        </div>
        <div
          className={`graph-unit-popup__body ${
            configuration.isPointsPanelOpen
              ? "graph-unit-popup__body--points-open"
              : ""
          }`}
        >
          <div className="graph-unit-popup__card">{content}</div>
          <section
            id={`${source.id}-saved-points`}
            className="graph-unit-popup__saved-points"
            aria-hidden={!configuration.isPointsPanelOpen}
          >
            <h3>saved points</h3>
            <p>Click a marked graph point again to remove it.</p>
            <SortableTableTool
              columns={savedPointColumns}
              rows={configuration.savedPoints}
              rowKey={(point) => point.id}
              emptyMessage="Click a tracked graph position to save its results."
              contextActions={[
                {
                  label: "remove saved point",
                  icon: <FaIcons.FaTrash />,
                  className: "danger",
                  action: removeSavedPoint,
                },
              ]}
            />
          </section>
        </div>
      </aside>
    </div>,
    document.body
  );
}
